/**
 * Holding the menu pill to talk.
 *
 * WHY A GESTURE AND NOT A BUTTON. On desktop the orb is cmd+/ and nothing
 * sits on screen until somebody asks for it; mobile has no chord, and the
 * orb is meant to appear only when it is wanted, so a permanent microphone
 * button in the chrome was the wrong trade. The floating menu pill is
 * already under the thumb, bottom centre, in the exact place the orb
 * appears, and it already steps aside while the orb is up. Holding it is
 * the pill becoming the orb.
 *
 * WHY NOT TRIPLE-TAP, which was the other idea. Scoped to the pill it would
 * work, but a mis-tap gives no warning and the thing being opened is a LIVE
 * MICROPHONE. A hold buzzes at the moment it arms, so the gesture announces
 * itself before it commits. Across the whole document it would be worse
 * again: impatient tapping at a slow list is not consent.
 *
 * SLOP IS THE WHOLE TRICK. A finger resting on the pill at the start of a
 * scroll must not open a conversation, so any real movement abandons the
 * press. `pointercancel` covers the case where the browser takes the
 * gesture over for scrolling, and the distance check covers the frames
 * before it decides.
 *
 * Pure, with `now` passed in, for the same reason the detector next door is:
 * a timing rule nobody can test is a timing rule nobody can change.
 */

export interface LongPressConfig {
  /**
   * How long the finger has to stay down.
   *
   * Below about 400ms it collides with a slow tap; much above 600ms and it
   * feels broken, because nothing has happened yet and there is no way to
   * know anything is going to.
   */
  holdMs: number
  /** How far the finger may wander before it counts as a scroll, in CSS px. */
  slopPx: number
}

export const DEFAULT_LONG_PRESS: LongPressConfig = {
  holdMs: 550,
  slopPx: 10,
}

export interface PressState {
  /**
   * `fired` outlives the release on purpose: the browser sends a click
   * after every touch, and without a phase to check, holding the pill would
   * open the orb AND toggle the menu underneath it.
   */
  phase: 'idle' | 'holding' | 'fired'
  x: number
  y: number
  startedAt: number
}

export type PressEvent =
  | { type: 'down'; x: number; y: number; at: number }
  | { type: 'move'; x: number; y: number }
  /** The hold timer, or any later frame. Pass the clock, not a promise. */
  | { type: 'elapsed'; at: number }
  | { type: 'up' }
  | { type: 'cancel' }
  /** The click the browser sends after a release, or a keyboard activation. */
  | { type: 'click' }

/**
 * `tap` is the ordinary press, emitted from the CLICK rather than the
 * release so a keyboard activation still counts: pointer events never fire
 * for Enter or Space, and the pill has to stay reachable without a finger.
 */
export type PressAction = 'long-press' | 'tap' | null

export const initialPress = (): PressState => ({
  phase: 'idle',
  x: 0,
  y: 0,
  startedAt: 0,
})

/** Advance the gesture by one event. State in, state and action out. */
export function step(
  state: PressState,
  event: PressEvent,
  config: LongPressConfig = DEFAULT_LONG_PRESS
): { state: PressState; action: PressAction } {
  switch (event.type) {
    case 'down':
      return {
        state: {
          phase: 'holding',
          x: event.x,
          y: event.y,
          startedAt: event.at,
        },
        action: null,
      }

    case 'move': {
      if (state.phase !== 'holding') return { state, action: null }
      const dx = event.x - state.x
      const dy = event.y - state.y
      if (Math.hypot(dx, dy) <= config.slopPx) return { state, action: null }
      // Moved. They are scrolling, not holding: abandon it silently, and
      // let the click that may follow be an ordinary tap.
      return { state: initialPress(), action: null }
    }

    case 'elapsed': {
      if (state.phase !== 'holding') return { state, action: null }
      if (event.at - state.startedAt < config.holdMs) {
        return { state, action: null }
      }
      // Armed WHILE THE FINGER IS STILL DOWN, deliberately. The buzz and the
      // orb arrive together, at the moment the rule is satisfied, so the
      // gesture confirms itself rather than waiting to see what happens on
      // release.
      return { state: { ...state, phase: 'fired' }, action: 'long-press' }
    }

    case 'up':
      // A release before the threshold is a tap, but it is the click that
      // says so. A release after it keeps `fired` so that click is eaten.
      if (state.phase === 'fired') return { state, action: null }
      return { state: initialPress(), action: null }

    case 'cancel':
      // The browser took the gesture for a scroll, or the finger left the
      // button. Either way nothing was meant by it.
      return { state: initialPress(), action: null }

    case 'click':
      if (state.phase === 'fired') {
        return { state: initialPress(), action: null }
      }
      return { state: initialPress(), action: 'tap' }
  }
}
